import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Globe, Instagram, MapPin, MessageCircle, Send } from "lucide-react";
import Logo from "./Logo";
import { BRAND, CONTACT, waLink } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_TYPES = ["Birthday Event", "Anniversary", "Welcome Baby", "Baby Shower", "Car Decoration", "Romantic Room Decoration", "Return Gifts", "Cakes"];

const inputCls =
  "w-full rounded-md border border-cream/20 bg-night/60 px-4 py-3 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors focus:border-gold";

function ContactRow({ icon, label, value, href, testid }) {
  const Wrapper = href ? "a" : "span";
  return (
    <Wrapper
      data-testid={testid}
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="group flex items-center gap-4 border-b border-cream/10 py-4 transition-colors"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold transition-all group-hover:bg-gold group-hover:text-night">
        {icon}
      </span>
      <span>
        <span className="block text-[10px] uppercase tracking-[0.3em] text-cream/50">{label}</span>
        <span className="block text-sm text-cream/90 sm:text-base">{value}</span>
      </span>
    </Wrapper>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", event_type: EVENT_TYPES[0], event_date: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [dateFocused, setDateFocused] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/enquiries`, form);
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="grain relative overflow-hidden bg-night py-24 text-cream sm:py-32">
      <Logo className="pointer-events-none absolute -right-10 top-10 h-64 w-64 opacity-[0.04]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">Book Us</p>
          <h2 className="font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
            Let&apos;s plan your
            <span className="font-script gold-foil-text pr-2 text-[1.15em]">next</span>
            <br />
            celebration.
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-cream/60">
            Share your date, place and idea — {BRAND.founder} personally replies with themes and
            a quote. The form reaches us instantly.
          </p>
          <div className="mt-10">
            <ContactRow testid="contact-whatsapp-link" icon={<MessageCircle className="h-4 w-4" />} label="WhatsApp" value={CONTACT.whatsappDisplay} href={waLink(`Hello ${BRAND.name}! I'd like to plan an event.`)} />
            <ContactRow testid="contact-instagram-link" icon={<Instagram className="h-4 w-4" />} label="Instagram" value={CONTACT.instagram} href={CONTACT.instagramUrl} />
            <ContactRow testid="contact-address-line" icon={<MapPin className="h-4 w-4" />} label="Address" value={CONTACT.address} />
            <ContactRow testid="contact-website-line" icon={<Globe className="h-4 w-4" />} label="Website" value={CONTACT.website} />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
          {status === "sent" ? (
            <div data-testid="enquiry-success-alert" className="flex h-full flex-col items-center justify-center rounded-xl border border-gold/40 bg-night/70 p-10 text-center">
              <MessageCircle className="mb-4 h-8 w-8 text-gold" />
              <h3 className="font-serif text-2xl">Enquiry received.</h3>
              <p className="mt-2 max-w-xs text-sm text-cream/60">
                Thank you! We&apos;ll get back to you shortly. For an instant reply, continue the
                conversation on WhatsApp.
              </p>
              <a
                data-testid="enquiry-success-whatsapp-button"
                href={waLink(`Hello ${BRAND.name}! I just sent an enquiry for ${form.event_type}.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-night transition-colors hover:bg-gold-light"
              >
                <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
              </a>
            </div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={submit} className="rounded-xl border border-cream/10 bg-night/60 p-7 backdrop-blur sm:p-9">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="enquiry-name-input" required minLength={2} placeholder="Your name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="enquiry-phone-input" required type="tel" placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} className={inputCls} />
                <select data-testid="enquiry-type-select" value={form.event_type} onChange={set("event_type")} className={inputCls}>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-night">{t}</option>
                  ))}
                </select>
                <input
                  data-testid="enquiry-date-input"
                  type={dateFocused || form.event_date ? "date" : "text"}
                  placeholder="Event Date"
                  min={new Date().toISOString().split("T")[0]}
                  value={form.event_date}
                  onFocus={() => setDateFocused(true)}
                  onBlur={() => setDateFocused(false)}
                  onChange={set("event_date")}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
              <textarea data-testid="enquiry-message-input" rows={4} placeholder="Tell us about the event — venue, guests, theme..." value={form.message} onChange={set("message")} className={`${inputCls} mt-4 resize-none`} />
              {status === "error" && (
                <p data-testid="enquiry-error-alert" className="mt-3 text-xs text-red-300">
                  Something went wrong — please try WhatsApp instead.
                </p>
              )}
              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={status === "sending"}
                className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-night transition-all hover:bg-gold-light disabled:opacity-60"
              >
                {status === "sending" ? "Sending..." : "Send Enquiry"} <Send className="h-4 w-4" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
